// Path: ./src/app/pages/StorePage/useStoreTableColumns.tsx
// setTableColumn.tsx

import { GridRenderCellParams } from "@mui/x-data-grid";
import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { useTranslation } from "react-i18next";
import {
  GfStore,
  GfStoreDto,
  SettingKey,
} from "@swagger/typescript-fetch-goflyer";
import { useNavigate } from "react-router-dom";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { RenderMultiImageTable } from "../../components/RenderImageTable";
import { useMemo } from "react";
import {
  useTableSettingsConfig,
  applyColumnsConfig,
} from "../../../hooks/useTableSettingsConfig";
import { routes } from "../../../utils/constant/navigationRoutes";

export const useStoreTableColumns = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { columnsConfig } = useTableSettingsConfig(SettingKey.TableStores);
  const values: AllowedTableColumn[] = ["updatedAt", "createdAt"];
  /**
   * Handles the click event to navigate to the store editing page with pre-filled data.
   *
   * @param value - The EditProps containing store details for editing.
   */
  const handleOnClickEdit = async (value: GfStoreDto) => {
    navigate(routes.store.editById(value?.id || ""));
  };

  const baseColumns = [
    {
      field: "edit",
      filterable: false,
      width: 90,
      headerName: `${t(messages.Edit())}`,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <PermissionComponentWrapper
            requiredPermission={["edit"]}
            requiredPermissionGroup={["store"]}
          >
            <RenderEditButton
              onClick={handleOnClickEdit}
              {...props}
            ></RenderEditButton>
          </PermissionComponentWrapper>
        );
      },
      sortable: false,
    },
    {
      filterable: false,
      field: "gfImages",
      headerName: `${t(messages["gfImages"]())}`,
      renderCell: (props: GridRenderCellParams<GfStore>) => {
        return (
          <RenderMultiImageTable
            images={[props.row.merchant.merchantLogo]}
          ></RenderMultiImageTable>
        );
      },
      sortable: false,
    },
    {
      field: "merchant",
      width: 180,
      headerName: `${t(messages["Merchant"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store.merchant.name}</div>;
      },
    },
    {
      field: "signature",
      width: 200,
      headerName: `${t(messages["Signature"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store?.signature}</div>;
      },
    },
    {
      field: "longitude",
      width: 150,
      type: "number",
      headerName: `${t(messages["longitude"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store.location?.coordinates[0]}</div>;
      },
    },
    {
      field: "latitude",
      type: "number",
      width: 150,
      headerName: `${t(messages["latitude"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store.location?.coordinates[1]}</div>;
      },
    },
    {
      field: "phone",
      width: 150,
      headerName: `${t(messages.Phone_Number())}`,
    },
    {
      field: "openTime",
      type: "number",
      width: 80,
      headerName: `Open Time`,
    },
    {
      field: "closeTime",
      width: 85,
      type: "number",
      headerName: `Close Time`,
    },
    {
      field: "storeTag",
      width: 200,
      headerName: `${t(messages["Store_Tag"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store?.storeTag?.map((val) => val.name).join(",")}</div>;
      },
    },
    {
      field: "city",
      width: 100,
      headerName: `${t(messages["City"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store?.city}</div>;
      },
    },
    {
      field: "province",
      width: 100,
      headerName: `${t(messages["Province"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store?.province}</div>;
      },
    },
    {
      field: "zip",
      width: 80,
      headerName: `${t(messages["Zip"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store?.zip}</div>;
      },
    },
    {
      field: "country",
      width: 80,
      headerName: `${t(messages["Country"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store?.country}</div>;
      },
    },
    {
      field: "slug",
      width: 200,
      headerName: `Slug`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfStore>) => {
        const store = params.row;
        return <div>{store?.slug}</div>;
      },
    },
    ...values.map((value) => {
      return {
        width: 200,
        field: value,
        headerName: `${t(messages[value]())}`,
        type: "dateTime",
        valueGetter: (val: GridRenderCellParams) => {
          return val && new Date(val?.row[value]);
        },
      };
    }),
    {
      field: "id",
      filterable: false,
      width: 300,
      headerName: `${t(messages["id"]())}`,
    },
  ];

  const columns = useMemo(
    () => applyColumnsConfig(baseColumns, columnsConfig),
    [baseColumns, columnsConfig],
  );

  return columns;
};
